import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  AttemptState,
  CalculatedOrderStatus,
  Delay,
  DeliveryAddress,
  DriverState,
  Problem,
  MlkAction,
  Vote,
  ShippedProductPaymentMethod,
} from '../../models';
import Card from '../Card/Card';
import { CardHeader } from '../CardHeader/CardHeader';
import { OrderSummaryBox } from '../OrderSummaryBox/OrderSummaryBox';
import './FrontCard.css';
import {
  calculateZoomForTwoPoint,
  getAdditionalInfo,
  getCardFooter,
  getFrontCardHomeButtons,
  getHeading,
  getImage,
} from './frontCardHelper';
import { LocationIcon } from '../Icons';
import { useLanguage } from '../../locale/useLanguage';
import { Stars } from '../Stars/Stars';
import { Config } from '../../helpers/config';
import { PinBox } from '../PinBox/PinBox';
import { TimeRange } from '../TimeRange/TimeRange';
import { ComposedMaps, markerIcons } from '../Map/Map';
import { getSummaryText } from '../OrderSummaryBox/orderSummaryBoxHelper';
import { useGetCmsConfigsQuery } from '../../apis/cmsApi/cms.hooks.api';
import { CreditCard } from '../Icons/CreditCard';

export type DriverInfo = {
  driverName: string;
  isArriving: boolean;
  eta: Dayjs;
  state: DriverState;
  happyImage?: string;
  sadImage?: string;
  lat?: number;
  lng?: number;
};

export type FrontCardProps = {
  headerLogoSrc: string;
  headerLogoAlt: string;
  noteForCustomer?: string;
  attempt?: AttemptState;
  orderState: CalculatedOrderStatus;
  address: DeliveryAddress;
  dateTime?: Dayjs | null;
  delay?: Delay;
  arrivingDate?: Dayjs;
  problem?: Problem;
  vote?: Vote;
  review?: string;
  poName?: string;
  territoryId?: number;
  poFromDate?: Dayjs;
  pinCode?: string;
  serviceDeadline: string;
  driverInfo?: DriverInfo;
  cashOnDeliveryAmount?: number;
  funnel?: { from: Dayjs; to: Dayjs } | null;
  availableActions: MlkAction[] | null;
  shippedProductPaymentMethod: ShippedProductPaymentMethod;
  isNoAssign?: boolean;
  isLastAttempt: boolean;
  onDetailsClick: () => void;
};

export const FrontCard: React.FC<FrontCardProps> = ({
  headerLogoAlt,
  headerLogoSrc,
  attempt,
  orderState,
  dateTime,
  arrivingDate,
  problem,
  delay,
  address,
  onDetailsClick,
  vote,
  review,
  poName,
  poFromDate,
  noteForCustomer,
  pinCode,
  funnel,
  driverInfo,
  serviceDeadline,
  isLastAttempt,
  cashOnDeliveryAmount,
  shippedProductPaymentMethod,
  availableActions,
  territoryId,
  isNoAssign,
}) => {
  const [t] = useLanguage();
  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const [pulseClass, setPulseClass] = useState('');
  const isInPo = Boolean(poFromDate && poName);
  const hasPos = useMemo(
    () =>
      !!cmsConfigs?.posCashOnDeliveryTerritories.find(
        (t) => t.territoryId === territoryId
      ),
    [cmsConfigs?.posCashOnDeliveryTerritories, territoryId]
  );

  const [zoom, setZoom] = useState(15);
  const [center, setCenter] = useState({ lat: address.lat, lng: address.lon });
  const containerMapRef = useRef<HTMLDivElement>(null);

  const isExpired = dayjs(serviceDeadline).isBefore(dayjs());

  const headerText = getHeading({
    driverName: driverInfo?.driverName,
    problem,
    isInPo,
    orderState,
    t,
  });
  const summaryText = getSummaryText(orderState, t);
  const homeButtons = useMemo(
    () =>
      getFrontCardHomeButtons(
        availableActions,
        orderState,
        cmsConfigs?.featureFlags.isPdrEnabled,
        problem,
        isExpired,
        isInPo
      ),
    [
      availableActions,
      cmsConfigs?.featureFlags.isPdrEnabled,
      isExpired,
      isInPo,
      orderState,
      problem,
    ]
  );

  const poOptions = useMemo(() => {
    if (isInPo) {
      return {
        hour: poFromDate?.format('HH:mm'),
        date: poFromDate?.format('DD/MM/YYYY'),
        poName,
      };
    }

    return undefined;
  }, [poFromDate, poName, isInPo]);

  const additionalInfos = getAdditionalInfo({
    isLastAttempt,
    problem,
    noteForCustomer,
    orderState,
    delay,
    poOptions,
    t,
  });

  const image = getImage({ orderState, showPoImage: isInPo });

  const cardFooterText = getCardFooter({
    cashOnDeliveryAmount,
    orderState,
    shippedProductPaymentMethod,
    pos: hasPos ? (
      <>
        <b>
          {t('home.total')} {cashOnDeliveryAmount}€
        </b>
        <div className="front-card--footer-pos">
          <p>{t('home.paymentViaPos')}</p>
          <CreditCard />
        </div>
      </>
    ) : undefined,
    t,
  });

  useEffect(() => {
    let timeoutRef: ReturnType<typeof setTimeout>;
    let intervalRef: ReturnType<typeof setInterval>;
    if (driverInfo?.isArriving) {
      intervalRef = setInterval(() => {
        setPulseClass('front-card--driver-pulse');
        timeoutRef = setTimeout(() => {
          setPulseClass('');
        }, 600);
      }, 10 * 1000);
    }

    return () => {
      timeoutRef && clearInterval(timeoutRef);
      intervalRef && clearInterval(intervalRef);
    };
  }, [driverInfo?.isArriving]);

  const orderSummaryStartTime =
    orderState === CalculatedOrderStatus.InTransit ||
    orderState === CalculatedOrderStatus.Scheduled
      ? funnel?.from
      : dateTime;

  const orderSummaryEndTime =
    orderState === CalculatedOrderStatus.InTransit ||
    orderState === CalculatedOrderStatus.Scheduled
      ? funnel?.to
      : arrivingDate;

  const calculateDistancePointsKmZoom = useCallback(() => {
    if (driverInfo?.lat && driverInfo.lng) {
      const { zoom, center } = calculateZoomForTwoPoint({
        firstLat: address.lat,
        secondLat: driverInfo?.lat,
        firstLng: address.lon,
        secondLng: driverInfo?.lng,
        height: containerMapRef.current?.offsetHeight,
        width: containerMapRef.current?.offsetWidth,
      });
      setZoom(zoom);
      setCenter(center);
    }
  }, [containerMapRef, address, driverInfo]);

  useEffect(() => {
    if (address && driverInfo?.lat && driverInfo.lng) {
      calculateDistancePointsKmZoom();
    }
  }, [calculateDistancePointsKmZoom, address, driverInfo]);

  const markers = useMemo(() => {
    if (driverInfo?.lat && driverInfo?.lng) {
      return [
        {
          icon: markerIcons.home,
          lat: address.lat,
          lng: address.lon,
        },
        {
          icon: markerIcons.truck,
          lat: driverInfo.lat,
          lng: driverInfo.lng,
        },
      ];
    } else {
      return [
        {
          icon: markerIcons.home,
          lat: address.lat,
          lng: address.lon,
        },
      ];
    }
  }, [address.lat, address.lon, driverInfo?.lat, driverInfo?.lng]);

  return (
    <>
      <Card footerText={cardFooterText} isGray={!hasPos}>
        <div className="front-card--container">
          <CardHeader
            imageAlt={headerLogoAlt}
            imageSrc={headerLogoSrc}
            title={headerText}
          />
          {image && (
            <div className="front-card--image-container">
              <img
                className="front-card--image"
                src={image}
                // TODO: Add alt attribute
              />
            </div>
          )}
          {!image && !(orderState === CalculatedOrderStatus.Arriving) && (
            <>
              <OrderSummaryBox
                attemptNumber={attempt}
                orderState={orderState}
                dateTime={orderSummaryStartTime}
                isInLate={delay !== Delay.No}
                singleText={summaryText}
                arrivingDate={orderSummaryEndTime}
                delay={delay}
              />
              <div className="front-card--location-container">
                <LocationIcon width="24px" height="24px" />
                <p className="front-card--location-text truncate">
                  {address.address}
                </p>
              </div>
              {vote && (
                <div className="front-card--stars-container">
                  <Stars
                    color={Config.primaryBlue}
                    useDefaultColor
                    filledStarsNumber={vote}
                    width={'20'}
                    height={'20'}
                  />
                </div>
              )}
              {review && <p className="front-card--review">{`"${review}"`}</p>}
            </>
          )}
          {driverInfo && orderState === CalculatedOrderStatus.Arriving && (
            <>
              <div ref={containerMapRef} className="front-card--map-container">
                <ComposedMaps
                  showPoi={false}
                  centerMap={{ lat: address.lat, lng: address.lon }}
                  isGestureEnabled
                  zoom={15}
                  currentCenterMap={center}
                  currentZoom={zoom}
                  markers={markers}
                />
                <div className="front-card--eta">
                  {t('summaryBox.at') + ' ' + driverInfo.eta.format('HH:mm')}
                </div>
                <div className="front-card--driver-image-container">
                  <img
                    className={`front-card--driver-image ${pulseClass}`}
                    src={driverInfo.sadImage}
                  />
                </div>
              </div>
              <p className="front-card--driver-state">
                {driverInfo.state === DriverState.Arraving
                  ? t('home.driverIsArriving')
                  : null}
                {driverInfo.state === DriverState.BubbleIn
                  ? t('home.driverIsParking')
                  : null}
                {driverInfo.state === DriverState.Arrived
                  ? t('home.driverHasParked')
                  : null}
              </p>
            </>
          )}
          {orderState === CalculatedOrderStatus.InTransit &&
            !driverInfo?.isArriving &&
            funnel &&
            dateTime &&
            arrivingDate && (
              <div className="font-card--time-range-container">
                <TimeRange
                  funnelStart={funnel!.from}
                  funnelEnd={funnel!.to}
                  startTime={dateTime!}
                  endTime={arrivingDate!}
                  delay={delay}
                />
              </div>
            )}
          {additionalInfos && (
            <p className="front-card--additional-info">{additionalInfos}</p>
          )}
          {orderState !== CalculatedOrderStatus.Deleted && (
            <div className="front-card--link-container">
              <a onClick={onDetailsClick} className="link front-card--link">
                {t('home.showDetails')}
              </a>
            </div>
          )}
        </div>
      </Card>
      {!isNoAssign ? (
        <div className="front-card--home-buttons-container">
          {homeButtons.map((hb, i) => {
            return (
              <div key={i} className="front-card--home-button">
                {hb}
              </div>
            );
          })}
        </div>
      ) : (
        <></>
      )}
      {orderState !== CalculatedOrderStatus.Deleted && (
        <div className="front-card--pin-box-container">
          <PinBox pin={pinCode!} />
        </div>
      )}
    </>
  );
};
